/**
 * Plausible Analytics initialization and event tracking.
 *
 * Same-origin first-party install: the script is served by our Express proxy
 * at /pa/script.js and events POST to /pa/event, so ad blockers see only our
 * own domain. Mirrors the lifecycle of metaPixel.ts.
 */
import { scrubRoute } from './scrubRoute';

type PlausibleCommand = [event: string, options?: PlausibleEventOptions];

interface PlausibleEventOptions {
  props?: Record<string, string | number | boolean | undefined>;
  callback?: () => void;
}

type PlausibleFunction = ((
  event: string,
  options?: PlausibleEventOptions
) => void) & {
  q?: PlausibleCommand[];
};

declare global {
  interface Window {
    plausible?: PlausibleFunction;
    _plausibleInitialized?: boolean;
  }
}

export const PLAUSIBLE_GOALS = {
  PROUPSELL_OPEN: 'ProUpsellOpen',
  PROUPSELL_SUBMIT: 'ProUpsellSubmit',
  NOT_FOUND: '404',
  CHUNK_LOAD_ERROR: 'ChunkLoadError',
} as const;

export type PlausibleGoal =
  (typeof PLAUSIBLE_GOALS)[keyof typeof PLAUSIBLE_GOALS];

const SCRIPT_SRC = '/pa/script.js';
const EVENT_API = '/pa/event';
const SCRIPT_SELECTOR = `script[src="${SCRIPT_SRC}"]`;
// Props that look like URL paths get ID-scrubbed before send so we don't blow
// up Plausible cardinality or leak record IDs into the dashboard.
const ROUTE_PROP_KEYS = new Set(['route', 'pathname', 'url']);

const ensurePlausibleStub = (): PlausibleFunction => {
  const existing = window.plausible;
  if (typeof existing === 'function') {
    return existing;
  }
  // Standard Plausible pre-load queue stub: arguments accumulated here are
  // replayed by the real script when it loads.
  const stub: PlausibleFunction = function plausibleStub(
    ...args: PlausibleCommand
  ) {
    stub.q = stub.q || [];
    stub.q.push(args);
  } as PlausibleFunction;
  stub.q = [];
  window.plausible = stub;
  return stub;
};

const scrubProps = (
  props: PlausibleEventOptions['props']
): PlausibleEventOptions['props'] => {
  if (!props) return props;
  const scrubbed: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(props)) {
    if (ROUTE_PROP_KEYS.has(key) && typeof value === 'string') {
      scrubbed[key] = scrubRoute(value);
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
};

interface InitializePlausibleArgs {
  domain: string;
  isAuthenticated?: boolean;
}

export const initializePlausible = ({
  domain,
  isAuthenticated = false,
}: InitializePlausibleArgs): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (!domain) {
    return Promise.resolve();
  }
  // Don't pollute visitor analytics with our own admin/student traffic.
  if (isAuthenticated) {
    return Promise.resolve();
  }
  if (window._plausibleInitialized) {
    return Promise.resolve();
  }
  if (document.querySelector(SCRIPT_SELECTOR)) {
    window._plausibleInitialized = true;
    return Promise.resolve();
  }

  ensurePlausibleStub();

  return new Promise(resolve => {
    try {
      const script = document.createElement('script');
      script.defer = true;
      script.src = SCRIPT_SRC;
      script.setAttribute('data-domain', domain);
      script.setAttribute('data-api', EVENT_API);
      script.onload = () => {
        window._plausibleInitialized = true;
        resolve();
      };
      script.onerror = () => {
        // Resolve rather than reject — analytics is best-effort, never block boot.
        resolve();
      };
      document.head.appendChild(script);
    } catch (err) {
      console.warn('Failed to inject Plausible script:', err);
      resolve();
    }
  });
};

export const trackPlausibleEvent = (
  goal: PlausibleGoal,
  props?: PlausibleEventOptions['props']
): void => {
  if (typeof window === 'undefined') return;
  const fn = window.plausible;
  if (typeof fn !== 'function') {
    return;
  }
  try {
    const scrubbed = scrubProps(props);
    if (scrubbed && Object.keys(scrubbed).length > 0) {
      fn(goal, { props: scrubbed });
    } else {
      fn(goal);
    }
  } catch (err) {
    console.warn('Failed to track Plausible event:', err);
  }
};

export const isPlausibleInitialized = (): boolean =>
  typeof window !== 'undefined' && window._plausibleInitialized === true;
